/**
 * AdminEarnings.jsx — All-doctors earnings + monthly payout tracker.
 * Embedded inside AdminDashboard.
 * Backend: GET /api/admin/earnings?month=YYYY-MM (optional) + /csv download.
 */
import React, { useEffect, useState } from "react";
import { Users, Download, RefreshCw, DollarSign } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { api, API } from "../lib/api";
import { toast } from "sonner";

const fmt = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

function monthOptions(count = 12) {
  const arr = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    arr.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return arr;
}

export default function AdminEarnings() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(""); // "" = lifetime
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const url = month ? `/admin/earnings?month=${month}` : "/admin/earnings";
      const r = await api.get(url);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load earnings");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [month]);

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem("noi_token") || localStorage.getItem("token");
      const url = `${API}/admin/earnings/csv${month ? `?month=${month}` : ""}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `noi-earnings-${month || "lifetime"}.csv`;
      link.click();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  if (loading || !data) {
    return <Card className="p-4 border-slate-200 mt-6 animate-pulse text-sm text-slate-400" data-testid="admin-earnings-loading">Loading payouts…</Card>;
  }

  const totals = data.totals || {};
  return (
    <Card className="p-5 border-slate-200 mt-6" data-testid="admin-earnings">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600"/>
          <h3 className="font-heading text-lg font-semibold text-slate-900">💰 Doctor Payouts</h3>
          <span className="text-xs text-slate-500">({data.doctor_count} doctors)</span>
        </div>
        <div className="flex items-center gap-2">
          <select className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm" value={month} onChange={e => setMonth(e.target.value)} data-testid="admin-earnings-month-filter">
            <option value="">Lifetime</option>
            {monthOptions().map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="admin-earnings-csv">
            <Download className="w-3.5 h-3.5 mr-1"/> CSV
          </Button>
          <Button variant="ghost" size="sm" onClick={load} data-testid="admin-earnings-refresh">
            <RefreshCw className="w-3.5 h-3.5"/>
          </Button>
        </div>
      </div>

      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Paid Cases</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{totals.paid_cases || 0}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Gross Revenue</div>
          <div className="mt-1 text-xl font-bold text-slate-900">{fmt(totals.revenue)}</div>
        </div>
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-800">Total Doctor Share</div>
          <div className="mt-1 text-xl font-bold text-emerald-900">{fmt(totals.doctor_share)}</div>
          <div className="text-[10px] text-emerald-700">70% payout</div>
        </div>
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-blue-800">Platform Share</div>
          <div className="mt-1 text-xl font-bold text-blue-900">{fmt(totals.platform_share)}</div>
          <div className="text-[10px] text-blue-700">30% keep</div>
        </div>
      </div>

      {/* Doctors table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-2">Doctor</th>
              <th className="py-2 pr-2 text-right">Paid</th>
              <th className="py-2 pr-2 text-right">Revenue</th>
              <th className="py-2 pr-2 text-right text-emerald-700">Doctor share</th>
              {month && <th className="py-2 pr-2 text-right">{month} share</th>}
              <th className="py-2 pr-2 text-right">Refunded</th>
              <th className="py-2 pr-2 text-right">Completed</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.doctor_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-earnings-row-${r.doctor_id}`}>
                <td className="py-2 pr-2">
                  <div className="font-semibold text-slate-900">{r.doctor_name}</div>
                  <div className="text-[10px] text-slate-500">{r.doctor_email}</div>
                </td>
                <td className="py-2 pr-2 text-right text-slate-700">{r.lifetime_paid_cases}</td>
                <td className="py-2 pr-2 text-right text-slate-700">{fmt(r.lifetime_revenue)}</td>
                <td className="py-2 pr-2 text-right text-emerald-700 font-semibold">{fmt(r.lifetime_doctor_share)}</td>
                {month && <td className="py-2 pr-2 text-right text-emerald-700">{fmt(r.month_doctor_share)}</td>}
                <td className="py-2 pr-2 text-right text-red-600">{r.lifetime_refunded_cases}</td>
                <td className="py-2 pr-2 text-right text-slate-600">{r.lifetime_completed}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
